import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Container,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import {
  formatCurrency,
  formatList,
} from 'renderer/shared/formatters/formatters';
import { useState, useEffect } from 'react';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectUserRole } from 'renderer/features/auth/state/userSelectors';
import {
  useGetProductDetailsQuery,
  useListProductsQuery,
} from '../services/products.endpoints';
import { GetProductDetailsResponse } from '../schemas/products.schemas';
import productThumbnails from '../constants/productThumbnails';
import ProductDeleteDialog from './ProductDeleteDialog';
import PurchaseDialog from './PurchaseDialog';
import FormattedDescription from './FormattedDescription';

const LoadingCard = () => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={4}>
        <Skeleton variant="rounded" data-testid="product-loading" />
      </Grid>
      <Grid item container spacing={3} direction="column" xs={8}>
        <Grid item>
          <Typography variant="h2">
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="h4">
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item>
          <Skeleton />
        </Grid>
      </Grid>
      <Grid item>
        <Skeleton />
      </Grid>
    </Grid>
  );
};

interface ErrorCardProps {
  error: FetchBaseQueryError | SerializedError;
}

const ErrorCard = ({ error }: ErrorCardProps) => {
  return (
    <Card>
      <CardHeader title="Error loading product details" />
      <CardContent>
        <Typography>
          {'status' in error && `${error.status}: ${error.data}`}
        </Typography>
      </CardContent>
    </Card>
  );
};

interface ProductDetailsProps {
  details: GetProductDetailsResponse;
  id: string;
}

const ProductDetails = ({ details, id }: ProductDetailsProps) => {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const role = useAppSelector(selectUserRole);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );

  // 🔁 Redirect logic - TEMPORARY FIX FOR CUSTOMERS GOING TO OLD FF2 LINK
  const restrictedProductId = '66341fef4895dadba053975d'; // FF2
  const newProductId = '67f6b5c241f8c9345e025d6d'; // FF3
  useEffect(() => {
    if ((role === 'user' || role === 'public') && id === restrictedProductId) {
      navigate(`/products/${newProductId}`, { replace: true });
    }
  }, [role, id, navigate]);

  const {
    data: products,
    isLoading,
    error,
  } = useListProductsQuery({ includeHidden: role === 'admin' });

  const {
    _id,
    name,
    fullDescription,
    briefDescription,
    productId,
    productPage,
    downloadPage,
    hidden,
    keywords,
    category,
    supportedHostApplications,
    supportedPlatforms,
    startingPrice,
    salePrice,
  } = details;
  const image = productThumbnails.get(productId) ?? '';
  const isOnSale = salePrice !== null && salePrice !== undefined;
  const isSubscription = category === 'Subscription';
  // const isOnSale = !!sale && new Date(sale.until) > new Date();

  if (!id) return null;

  const handlePurchaseClick = (
    pid: string,
    productCategory: string,
    productName: string,
  ) => {
    if (
      productCategory === 'Video Plugin' &&
      productName.toLowerCase().indexOf('anarchy complete bundle') < 0 // skip the dialog for Anarchy Complete Bundle
    ) {
      setSelectedProductId(pid);
      setModalOpen(true);
    } else {
      navigate(`/purchase/${pid}`);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProductId(null);
  };

  const handleNavigate = (path: string) => {
    setModalOpen(false);
    navigate(path);
  };

  return (
    <Container maxWidth="lg">
      <Grid container spacing={3}>
        <Grid item xs={4}>
          <Tooltip title={keywords}>
            <Card data-testid="product-keywords">
              <CardMedia
                component="img"
                image={image}
                alt={name}
                data-testid="product-image"
              />
              <CardActions>
                <Box>
                  {/* <Link to={`/purchase/${id}`}> */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePurchaseClick(_id, category, name);
                    }}
                  >
                    Purchase
                  </Button>
                  {/* </Link> */}
                </Box>
                {role === 'admin' && (
                  <Box>
                    <Link to={`/products/edit/${id}`}>
                      <Button variant="contained" color="secondary">
                        Edit
                      </Button>
                    </Link>
                  </Box>
                )}
                {role === 'admin' && (
                  <Box>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      Delete
                    </Button>
                  </Box>
                )}
                <ProductDeleteDialog
                  open={deleteDialogOpen}
                  setOpen={setDeleteDialogOpen}
                  id={id}
                />
              </CardActions>
            </Card>
          </Tooltip>
        </Grid>
        <Grid item container spacing={1.2} direction="column" xs={8}>
          <Grid item>
            <Typography variant="h3">{name}</Typography>
          </Grid>
          {hidden && (
            <Grid item>
              <Tooltip title="This item is hidden from customers.">
                <VisibilityOffIcon data-testid="product-hidden" />
              </Tooltip>
            </Grid>
          )}
          <Grid item>
            <Typography
              variant="h4"
              display="inline-block"
              sx={{ textDecorationLine: isOnSale ? 'line-through' : 'none' }}
            >
              {formatCurrency(startingPrice)}
            </Typography>{' '}
            {isOnSale && (
              <Typography variant="h4" display="inline-block">
                {formatCurrency(salePrice)}
              </Typography>
            )}{' '}
            {isSubscription && (
              <Typography variant="h4" display="inline-block">
                per year
              </Typography>
            )}
          </Grid>
          <Grid item>
            <Typography>{category}</Typography>
          </Grid>
          <Grid item>
            <FormattedDescription text={briefDescription} />
          </Grid>
          <Grid item>
            <Typography>
              Need more info and examples? Check out the{' '}
              <Link to={productPage} target="_blank" rel="noopener noreferrer">
                Product Page
              </Link>
            </Typography>
          </Grid>
          <Grid item>
            <Typography>
              Want to give it a try?{' '}
              <Link to={downloadPage} target="_blank" rel="noopener noreferrer">
                Download a copy
              </Link>
              .
            </Typography>
          </Grid>
          <Grid item>
            <Typography>
              Supported on <b>{formatList(supportedPlatforms)}</b>.
            </Typography>
          </Grid>
          <Grid item>
            <Typography>
              Use it in{' '}
              <b>
                {formatList(
                  supportedHostApplications.map((app) => app.displayName),
                )}
              </b>
              .
            </Typography>
          </Grid>
        </Grid>
        <Grid item>
          <FormattedDescription text={fullDescription} />
        </Grid>
      </Grid>
      <PurchaseDialog
        products={products as any[]}
        open={modalOpen}
        onClose={handleCloseModal}
        onNavigate={handleNavigate}
        productId={selectedProductId}
      />
    </Container>
  );
};

const ProductDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const role = useAppSelector(selectUserRole);
  const {
    data: details,
    error,
    isLoading,
  } = useGetProductDetailsQuery(id ?? '');

  return (
    <Stack spacing={3} sx={{ padding: 3 }}>
      <Link to="/">
        <Button startIcon={<ArrowBackIcon />}>Back to All Products</Button>
      </Link>
      {isLoading && <LoadingCard />}
      {error && <ErrorCard error={error} />}
      {details && <ProductDetails details={details} id={id ?? ''} />}
    </Stack>
  );
};

export default ProductDetailsPage;
